import { BrowserMultiFormatReader } from "@zxing/browser";
import template from "./badge_scanner.html";
import en from "./en.json";
import es from "./es.json";
import {
    FACING_MODE_ENVIRONMENT,
    FACING_MODE_USER,
} from "../../common.constants";

const DETECTION_INTERVAL_MS = 500;

class BadgeScannerController {
    /*@ngInject*/
    constructor($timeout, $window, $q, $interval, $element) {
        this.$timeout = $timeout;
        this.$window = $window;
        this.$q = $q;
        this.$interval = $interval;
        this.$element = $element;
        this.cameraAllowed = true;
    }

    $onInit() {
        let ctrl = this;
        ctrl.activeCamera =
            ctrl.defaultCamera || ctrl.facingMode || FACING_MODE_ENVIRONMENT;
        ctrl.startTimeout = ctrl.$timeout(function () {
            ctrl.video = ctrl.$element[0].querySelector("video");
            ctrl.startScanner();
        });
    }

    startScanner() {
        let ctrl = this;
        if (ctrl.destroyed) {
            return;
        }
        const session = { active: true, detecting: false };
        ctrl.scanSession = session;
        ctrl.startingCamera = true;
        ctrl.cameraAllowed = true;
        let options = {
            video: {
                width: { ideal: 1920 },
                height: { ideal: 1080 },
                facingMode: ctrl.activeCamera,
                frameRate: { ideal: 30 },
            },
            audio: false,
        };
        ctrl.$q
            .when()
            .then(function () {
                if (!session.active) {
                    return;
                }
                if (ctrl.$window.BarcodeDetector) {
                    return ctrl.captureVideo(options, session);
                }
                return ctrl.startFallbackReader(options, session);
            })
            .catch(function (error) {
                if (session.active) {
                    ctrl.cameraAllowed = false;
                    ctrl.onError({ error });
                }
            })
            .finally(function () {
                if (session.active) {
                    ctrl.startingCamera = false;
                }
            });
    }

    switchCamera() {
        let ctrl = this;
        if (ctrl.destroyed || ctrl.startingCamera) {
            return;
        }
        ctrl.stopScanner();
        ctrl.activeCamera =
            ctrl.activeCamera === FACING_MODE_USER
                ? FACING_MODE_ENVIRONMENT
                : FACING_MODE_USER;
        ctrl.startScanner();
    }

    startFallbackReader(options, session) {
        let ctrl = this;
        const codeReader = new BrowserMultiFormatReader();
        return ctrl.$q
            .when(
                codeReader.decodeFromConstraints(
                    options,
                    ctrl.video,
                    function (result, error, controls) {
                        if (!session.active) {
                            controls.stop();
                            return;
                        }
                        ctrl.readerControls = controls;
                        if (result) {
                            ctrl.$q.when().then(function () {
                                if (session.active) {
                                    ctrl.reportResult(result.text);
                                }
                            });
                        }
                    },
                ),
            )
            .then(function (controls) {
                if (!session.active) {
                    controls.stop();
                } else {
                    ctrl.readerControls = controls;
                }
            });
    }

    captureVideo(options, session) {
        let ctrl = this;
        const barcodeDetector = new ctrl.$window.BarcodeDetector({
            formats: ["qr_code", "code_128"],
        });
        return ctrl.$q
            .when(ctrl.$window.navigator.mediaDevices.getUserMedia(options))
            .then(function (stream) {
                if (!session.active) {
                    stream.getTracks().forEach(function (track) {
                        track.stop();
                    });
                    return;
                }
                ctrl.video.srcObject = stream;
                return ctrl.video.play();
            })
            .then(function () {
                if (!session.active) {
                    return;
                }
                ctrl.readerInterval = ctrl.$interval(function () {
                    if (
                        !session.active ||
                        session.detecting ||
                        ctrl.video.readyState < ctrl.video.HAVE_CURRENT_DATA
                    ) {
                        return;
                    }
                    session.detecting = true;
                    ctrl.$q
                        .when(barcodeDetector.detect(ctrl.video))
                        .then(function (barcodes) {
                            if (session.active && barcodes[0]) {
                                ctrl.reportResult(barcodes[0].rawValue);
                            }
                        })
                        .catch(function () {})
                        .finally(function () {
                            session.detecting = false;
                        });
                }, DETECTION_INTERVAL_MS);
            });
    }

    reportResult(value) {
        let ctrl = this;
        if (!ctrl.destroyed && ctrl.lastScannedValue !== value) {
            ctrl.lastScannedValue = value;
            ctrl.onSuccess({ value });
        }
    }

    $onDestroy() {
        let ctrl = this;
        ctrl.destroyed = true;
        ctrl.$timeout.cancel(ctrl.startTimeout);
        ctrl.stopScanner();
    }

    stopScanner() {
        let ctrl = this;
        if (ctrl.scanSession) {
            ctrl.scanSession.active = false;
        }
        ctrl.$interval.cancel(ctrl.readerInterval);
        ctrl.readerInterval = null;
        if (ctrl.readerControls) {
            ctrl.readerControls.stop();
            ctrl.readerControls = null;
        }
        ctrl.endVideoCapture();
    }

    endVideoCapture() {
        let ctrl = this;
        if (ctrl.video && ctrl.video.srcObject) {
            ctrl.video.srcObject.getTracks().forEach(function (track) {
                track.stop();
            });
            ctrl.video.srcObject = null;
        }
    }
}

export default {
    controller: BadgeScannerController,
    template: template,
    translations: { en, es },
    bindings: {
        onSuccess: "&",
        onError: "&",
        defaultCamera: "<?",
        facingMode: "<?",
    },
    name: "badgeScanner",
};
